/**
 * This exposes functions that will return true when a feature toggle has been set.
 * It will work both on the server and client
 *
 * To enable a feature:
 *
 *  1) Find the feature name in the `Feature` type below, for instance 'MYACCOUNT'
 *  2) Set an env var using the FEATURE_ prefix when starting the server, ie FEATURE_MYACCOUNT
 *  3) Run the app with `FEATURE_MYACCOUNT=true yarn serve` in dev mode or you can add it the .env file
 *
 * To add a feature toggle
 *
 *  1) Add a name to the Feature type, AWESOME_SAUCE for example,
 *      ex: `| 'AWESOME_SAUCE'`
 *  2) Export a function named isAwesomeSauceEnabled
 *      ex: `export const isAwesomeSauceEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'AWESOME_SAUCE')`
 *  3) To enable it see above
 */

import getConfig from 'next/config'

import { Feature } from '@grandvision/state'

export type FeatureCheck = () => boolean

let enabledFeatures: string[] | undefined

const getEnabledFeatures = () => {
  const features = getConfig().publicRuntimeConfig.grandvision.features as string[]

  if (typeof window === 'undefined') {
    console.info(`> Feature toggles enabled: ${features.length ? features : '-'}`)
  }

  return features
}

const isFeatureEnabled = (feature: Feature): boolean => {
  if (!enabledFeatures) {
    enabledFeatures = getEnabledFeatures()
  }
  return enabledFeatures.some((e) => e.toUpperCase() === feature.toUpperCase())
}

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isAABEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'AAB')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isMyAccountEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'MYACCOUNT')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isAddressBookEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'ADDRESSBOOK')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isContactLensEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'CONTACTLENS')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isContactLensStockEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'CONTACTLENS_STOCK'
)

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isSolutionsEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'SOLUTIONS')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isSearchEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'SEARCH')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isVtoEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'VTO')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isGPSEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'GPS_STORES')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isPlacePredictionsEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'STORES_PLACE_PREDICTIONS'
)

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isMultilingualEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'MULTILINGUAL')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isReviewsEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'REVIEWS')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isABTestingEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'ABTESTING')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isCrossSellEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'CROSS_SELL')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isPrescriptionGlassesEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'PRESCRIPTION_GLASSES'
)

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isPrescriptionGlassesReorderEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'PRESCRIPTION_GLASSES_REORDER'
)

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isPrescriptionGlassesConfiguratorValidationStepEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'PRESCRIPTION_GLASSES_CONFIGURATOR_VALIDATION_STEP'
)

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isPrescriptionFileEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'PRESCRIPTION_FILE'
)

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isSubscriptionHasOneAddressEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'SUBSCRIPTION_HAS_ONE_ADDRESS'
)

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isSubscriptionUpdateAddressEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'SUBSCRIPTION_UPDATE_ADDRESS'
)

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isCartV2Enabled: FeatureCheck = isFeatureEnabled.bind(null, 'CART_V2')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isStoreSearchEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'STORE_SEARCH')

/**
 * @deprecated use the new useFeatureToggles() hook in @grandvision/state
 */
export const isPreferredContactMethodEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'PREFERRED_CONTACT_METHOD'
)
